import React from "react";
import PropTypes from "prop-types";
import { maxBy, minBy as _minBy } from "lodash";
import { toDateString } from "../../helpers/converters";
import { Tooltip } from "antd";

const styles = {
  timelineContainer: {
    padding: "135px 20px 0px 20px",
    overflowX: "auto",
    overflowY: "visible"
  },
  timelineInnerContainer: {
    borderTop: "1px solid #d4dbe3",
    minWidth: 750,
    width: "80%",
    marginLeft: "auto",
    marginRight: "auto",
    position: "relative",
    padding: 0
  },
  mainLine: {
    position: "relative",
    margin: "0 0 35px 0",
    height: 1,
    marginTop: -1
  },
  markersContainer: {
    position: "relative",
    margin: 0
  },
  markers: {
    height: 70
  },
  point: {
    position: "absolute",
    top: 0,
    height: 21,
    width: 21,
    marginTop: -12.5,
    marginLeft: -12.5,
    border: "2px solid #3eabe1",
    borderRadius: 13,
    backgroundColor: "#ffffff",
    boxShadow: "0px 0px 0px 4px rgba(204,235,250,1)",
    zIndex: 3,
    cursor: "pointer"
  },
  pointSelected: {
    backgroundColor: "#3eabe1"
  },
  pointUnimportant: {
    height: 15,
    width: 15,
    marginTop: -7.5,
    marginLeft: -7.5,
    border: "1px solid #F89A4B",
    boxShadow: "0px 0px 0px 1px #facda8",
    zIndex: 2
  },
  pointUnimportantSelected: {
    backgroundColor: "#F89A4B"
  },
  label: {
    position: "absolute",
    bottom: 0,
    paddingLeft: 2,
    zIndex: 1,
    fontSize: "65%",
    fontWeight: 600,
    textTransform: "uppercase",
    borderLeft: "1px dotted #d4dbe3"
  },
  marker: {
    position: "absolute",
    display: "inline-block",
    verticalAlign: "top",
    backgroundColor: "#d4dbe3",
    top: 0,
    width: 1,
    height: 20
  },
  markerYear: {
    height: 50
  },
  markerLabel: {
    position: "absolute",
    bottom: 0,
    width: 100,
    textAlign: "center",
    marginLeft: -50,
    marginBottom: -15,
    color: "#d4dbe3",
    fontWeight: 600,
    fontSize: "75%"
  },
  markerYearLabel: {
    fontSize: "90%"
  }
};

const Label = props => {
  const style = {
    ...styles.label,
    left: `${props.positionX}%`,
    paddingBottom: props.positionY
  };
  return <div style={style}>{props.text}</div>;
};

Label.propTypes = {
  positionX: PropTypes.number,
  positionY: PropTypes.number,
  text: PropTypes.string
};

const Point = props => {
  const style = {
    left: `${props.positionX}%`,
    ...styles.point,
    ...(props.unimportant ? styles.pointUnimportant : {}),
    ...(props.selected ? (props.unimportant ? styles.pointUnimportantSelected : styles.pointSelected) : {})
  };
  return (
    <Tooltip title={`${props.data.label} : ${toDateString(props.data.date)}`}>
      <div onClick={props.onClick} style={style} />
    </Tooltip>
  );
};

Point.propTypes = {
  positionX: PropTypes.number,
  unimportant: PropTypes.bool,
  selected: PropTypes.bool,
  onClick: PropTypes.func,
  data: PropTypes.shape({
    date: PropTypes.string,
    label: PropTypes.string,
    key: PropTypes.any
  })
};

const Marker = props => {
  const markerStyle = {
    ...styles.marker,
    ...(props.type === "year" ? styles.markerYear : {}),
    left: `${props.positionX}%`
  };
  const markerLabelStyle = {
    ...styles.markerLabel,
    ...(props.type === "year" ? styles.markerYearLabel : {}),
    left: `${props.positionX}%`
  };
  return (
    <div style={markerStyle}>
      <div style={markerLabelStyle}>{props.label}</div>
    </div>
  );
};

Marker.propTypes = {
  positionX: PropTypes.number,
  type: PropTypes.oneOf(["year", "month"]),
  label: PropTypes.string
};

const TimeLine = props => {
  const { numMonths, monthMarkerLabels, data, importantLabels, style, isSelected, onPointSelection, cycleSize } = props;

  const diffDays = (d1, d2) => Math.round((d1 - d2) / (1000 * 60 * 60 * 24));
  const earliestDate = new Date(_minBy(data, "date").date);
  const startDate = new Date(earliestDate.getFullYear(), earliestDate.getMonth());
  const latestDate = new Date(maxBy(data, "date").date);
  const fixedLastDate = new Date(startDate).setMonth(startDate.getMonth() + numMonths);
  const dynamicLastDate = new Date(latestDate.getFullYear(), latestDate.getMonth() + 3);
  const lastDate = Math.max(fixedLastDate, dynamicLastDate);
  const totalDays = diffDays(lastDate, startDate);
  const dayPercentage = 100 / totalDays;

  // render labels
  let markers = [];
  // iterate all months between startDate and lastDate
  for (let iterDate = new Date(startDate); iterDate < lastDate; iterDate.setDate(iterDate.getDate() + 1)) {
    if (iterDate.getDate() === 1) {
      const month = iterDate.getMonth();
      const type = month === 0 ? "year" : "month";
      const markerLabel = month === 0 ? `${iterDate.getFullYear()}` : monthMarkerLabels[month];
      const positionX = diffDays(iterDate, startDate) * dayPercentage;
      markers.push(<Marker key={iterDate.getTime()} label={markerLabel} type={type} positionX={positionX} />);
    }
  }

  // render points and labels
  let points = [];
  let labels = [];
  let idxImportant = 0;
  data.forEach((d, i) => {
    const isUnimportant = importantLabels.indexOf(d.label) === -1;
    const dayDiff = diffDays(new Date(d.date), startDate);
    const positionX = dayDiff * dayPercentage;
    const selected = isSelected(d);
    points.push(<Point onClick={onPointSelection.bind(null, d.key)} data={d} selected={selected} key={i} positionX={positionX} unimportant={isUnimportant} />);
    if (!isUnimportant) {
      const positionY = 35 + (100 / cycleSize) * (idxImportant % cycleSize);
      labels.push(<Label key={`label-${i}`} positionY={positionY} positionX={positionX} text={d.label} />);
      idxImportant += 1;
    }
  });
  return (
    <div style={(style, styles.timelineContainer)}>
      <div style={styles.timelineInnerContainer}>
        <div style={styles.mainLine}>
          {points}
          {labels}
        </div>
        <div style={styles.markersContainer}>
          <div style={styles.markers}>{markers}</div>
        </div>
      </div>
    </div>
  );
};

TimeLine.propTypes = {
  numMonths: PropTypes.number,
  monthMarkerLabels: PropTypes.array,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string,
      label: PropTypes.string,
      key: PropTypes.any
    })
  ).isRequired,
  importantLabels: PropTypes.array.isRequired,
  onPointSelection: PropTypes.func,
  isSelected: PropTypes.func,
  cycleSize: PropTypes.number
};

TimeLine.defaultProps = {
  numMonths: 24,
  monthMarkerLabels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
  onPointSelection: () => undefined,
  isSelected: () => false,
  cycleSize: 3
};

export default TimeLine;
